import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { usePublicClient, useWriteContract } from "wagmi";
import { formatEther, parseEther } from "viem";
import AuctionCountdown from "../components/common/AuctionCountdown";
import NftCard from "../components/common/NftCard";
import Panel from "../components/common/Panel";
import Seo from "../components/common/Seo";
import auctionAbi from "../abi/AuctionContract.json";
import { t } from "../i18n/messages";
import { useAuctionSocket } from "../hooks/useAuctionSocket";
import { useAppState } from "../state/AppStateProvider";
import { buildPath } from "../utils/routing";
import {
  AUCTION_CONTRACT_ADDRESS,
  AUCTION_QUOTE_SYMBOL,
  USDT_CONTRACT_ADDRESS,
} from "../utils/contractConstants";
import {
  apiGetAuctions,
  apiGetMarketNfts,
  apiGetUserAuctionBids,
  apiGetAuctionRebates,
  apiGetAuctionEvents,
  apiNotifyAuctionTx,
} from "../api/backend";
import { formatAmount, formatDateTime } from "../utils/format";
import {
  buildNftIdentityKey,
  buildNftIdentityKeyFromRow,
  resolveNftAddress,
  resolveNftGalleryImages,
  resolveNftImage,
  resolveTokenId,
} from "../utils/nftMedia";
import {
  buildAuctionEventKey,
  buildBidEventKey,
  buildBscTxUrl,
  buildRebateDisplayKey,
  buildRebateKey,
  computeBidStepWei,
  computeNextBidFromBaseWei,
  computeNextBidWei,
  dedupeRows,
  formatCountdownSec,
  formatTinyAmount,
  isNotEnoughPaymentError,
  normalizeLowerAddress,
  parseToSec,
  resolveAmountValue,
  resolveBidBaseWei,
  resolveBidder,
  resolveCreatedAtValue,
  resolveEventType,
  resolveRecipient,
  resolveTxHash,
  shortHash,
} from "../utils/auctionDetail";

const ERC20_ABI = [
  {
    type: "function",
    name: "allowance",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "", type: "bool" }],
  },
];
const USDT_BID_APPROVAL_WEI = parseEther("1000");
const REBATE_PAGE_SIZE = 10;
const BID_PAGE_SIZE = 10;

export default function AuctionDetailPage({ locale }) {
  const navigate = useNavigate();
  const { auctionId } = useParams();
  const { walletConnected, walletAddress } = useAppState();
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();

  const [bid, setBid] = useState({ status: "idle", message: "" });
  const [events, setEvents] = useState([]);
  const [liveRebates, setLiveRebates] = useState([]);
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  const [rebatePage, setRebatePage] = useState(1);
  const [bidPage, setBidPage] = useState(1);
  const [refreshingBid, setRefreshingBid] = useState(false);
  const [refreshTip, setRefreshTip] = useState("");
  const [chainBidBaseWei, setChainBidBaseWei] = useState(null);

  const auctionsQuery = useQuery({
    queryKey: ["auction", "detail", auctionId],
    queryFn: () => apiGetAuctions({ limit: 1, auctionId }),
    staleTime: 15_000,
    retry: 1,
  });

  const bidsQuery = useQuery({
    queryKey: ["auction", "bids", walletAddress, auctionId],
    queryFn: () =>
      apiGetUserAuctionBids(walletAddress, { limit: 200, auctionId }),
    enabled: Boolean(walletConnected && walletAddress),
    staleTime: 15_000,
    retry: 1,
  });

  const rebatesQuery = useQuery({
    queryKey: ["auction", "rebates", auctionId],
    queryFn: () => apiGetAuctionRebates(auctionId, { limit: 200 }),
    staleTime: 120_000,
    gcTime: 600_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const eventsQuery = useQuery({
    queryKey: ["auction", "events", auctionId],
    queryFn: () => apiGetAuctionEvents(auctionId, { limit: 200 }),
    staleTime: 120_000,
    gcTime: 600_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const nftCatalogQuery = useQuery({
    queryKey: ["market", "nfts", "auction-detail", locale],
    queryFn: async () => {
      const result = await apiGetMarketNfts(500, locale);
      return Array.isArray(result?.nfts) ? result.nfts : [];
    },
    staleTime: 60_000,
    retry: 1,
  });

  const auction = useMemo(() => {
    const data = auctionsQuery.data;
    return (
      data?.auction ??
      (Array.isArray(data?.auctions) ? data.auctions[0] : null) ??
      null
    );
  }, [auctionsQuery.data]);

  const myBid = useMemo(() => {
    const bids = bidsQuery.data?.bids;
    if (!Array.isArray(bids) || !bids.length) return null;
    // DB column is "amount", not "bid_amount"
    return bids[0]?.amount ?? null;
  }, [bidsQuery.data]);

  const quoteSymbol = AUCTION_QUOTE_SYMBOL;
  const auctionTokenId = resolveTokenId(auction);
  const auctionTypeLabel = t(locale, "auctionTypeEnglish");

  const nftInfoByKey = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(nftCatalogQuery.data)
      ? nftCatalogQuery.data
      : [];
    rows.forEach((row) => {
      const key = buildNftIdentityKeyFromRow(row);
      if (!key || map.has(key)) return;
      map.set(key, {
        image: resolveNftImage(row),
        galleryImages: resolveNftGalleryImages(row),
        name: String(row?.name || "").trim(),
        description: String(row?.description || "").trim(),
      });
    });
    return map;
  }, [nftCatalogQuery.data]);

  const auctionNftInfo = useMemo(() => {
    const key = buildNftIdentityKey(resolveNftAddress(auction), auctionTokenId);
    return nftInfoByKey.get(key) || {};
  }, [auction, auctionTokenId, nftInfoByKey]);

  const auctionImage = useMemo(() => {
    const direct = resolveNftImage(auction);
    if (direct) return direct;
    return auctionNftInfo.image || "";
  }, [auction, auctionNftInfo.image]);

  const auctionGalleryImages = useMemo(() => {
    const directGallery = resolveNftGalleryImages(auction);
    if (directGallery.length > 0) return directGallery;
    return auctionNftInfo.galleryImages || [];
  }, [auction, auctionNftInfo.galleryImages]);

  const artworkName = useMemo(
    () =>
      String(
        auction?.name ||
          auctionNftInfo.name ||
          `${t(locale, "auctionColAsset")} #${auctionTokenId || "-"}`,
      ).trim(),
    [auction?.name, auctionNftInfo.name, locale, auctionTokenId],
  );

  const artworkDescription = useMemo(
    () =>
      String(auction?.description || auctionNftInfo.description || "").trim(),
    [auction?.description, auctionNftInfo.description],
  );

  const minBidAmount = useMemo(() => {
    const nextBidWei =
      chainBidBaseWei && chainBidBaseWei > 0n
        ? computeNextBidFromBaseWei(chainBidBaseWei)
        : computeNextBidWei(auction);
    if (nextBidWei <= 0n) return "";
    return formatTinyAmount(formatEther(nextBidWei));
  }, [auction, chainBidBaseWei]);

  const mergedEvents = useMemo(() => {
    const historical = Array.isArray(eventsQuery.data?.events)
      ? eventsQuery.data.events
      : [];
    return dedupeRows([...events, ...historical], buildAuctionEventKey);
  }, [events, eventsQuery.data?.events]);

  const mergedBidEvents = useMemo(() => {
    const bids = mergedEvents.filter((row) => {
      const eventType = resolveEventType(row);
      return eventType === "bid";
    });
    return dedupeRows(bids, buildBidEventKey);
  }, [mergedEvents]);

  const bidPageCount = useMemo(
    () => Math.max(1, Math.ceil(mergedBidEvents.length / BID_PAGE_SIZE)),
    [mergedBidEvents.length],
  );
  const activeBidPage = Math.min(Math.max(1, bidPage), bidPageCount);
  const pagedBidEvents = useMemo(() => {
    const start = (activeBidPage - 1) * BID_PAGE_SIZE;
    return mergedBidEvents.slice(start, start + BID_PAGE_SIZE);
  }, [mergedBidEvents, activeBidPage]);

  const mergedRebates = useMemo(() => {
    const dbRebates = Array.isArray(rebatesQuery.data?.rebates)
      ? rebatesQuery.data.rebates
      : [];
    const strictMerged = dedupeRows(
      [...liveRebates, ...dbRebates],
      buildRebateKey,
    );
    return dedupeRows(strictMerged, buildRebateDisplayKey);
  }, [liveRebates, rebatesQuery.data?.rebates]);

  const rebatePageCount = useMemo(
    () => Math.max(1, Math.ceil(mergedRebates.length / REBATE_PAGE_SIZE)),
    [mergedRebates.length],
  );
  const activeRebatePage = Math.min(Math.max(1, rebatePage), rebatePageCount);

  const pagedRebates = useMemo(() => {
    const start = (activeRebatePage - 1) * REBATE_PAGE_SIZE;
    return mergedRebates.slice(start, start + REBATE_PAGE_SIZE);
  }, [mergedRebates, activeRebatePage]);

  const rebateTotals = useMemo(() => {
    const totals = {};
    for (const row of mergedRebates) {
      const addr = resolveRecipient(row).toLowerCase();
      const amount = Number(resolveAmountValue(row));
      if (!addr || amount <= 0) continue;
      totals[addr] = (totals[addr] || 0) + amount;
    }
    return totals;
  }, [mergedRebates]);

  const myAddr = String(walletAddress || "")
    .trim()
    .toLowerCase();
  const myRebateTotal = myAddr ? rebateTotals[myAddr] || 0 : 0;

  const listingStartSec = useMemo(() => {
    return parseToSec(auction?.start_time ?? 0);
  }, [auction]);

  const bidUnlockAtSec = listingStartSec;
  const bidCountdownSec = bidUnlockAtSec
    ? Math.max(0, bidUnlockAtSec - nowSec)
    : 0;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNowSec(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setRebatePage((prev) => Math.min(prev, rebatePageCount));
  }, [rebatePageCount]);

  useEffect(() => {
    setBidPage((prev) => Math.min(prev, bidPageCount));
  }, [bidPageCount]);

  useEffect(() => {
    setChainBidBaseWei(null);
  }, [auctionId]);

  useAuctionSocket({
    auctionId,
    enabled: Boolean(auctionId),
    onEvent: (msg) => {
      const eventType = resolveEventType(msg);
      if (eventType === "rebate") {
        setLiveRebates((prev) =>
          dedupeRows([msg, ...prev], buildRebateKey).slice(0, 50),
        );
        return;
      }

      setEvents((prev) =>
        dedupeRows([msg, ...prev], buildAuctionEventKey).slice(0, 20),
      );
      if (eventType === "bid") {
        auctionsQuery.refetch();
        bidsQuery.refetch();
      }
    },
  });

  const runPlaceBid = async () => {
    const setBidError = (message) => setBid({ status: "error", message });

    if (bid.status === "loading") return;
    if (!walletConnected || !walletAddress) {
      setBidError(t(locale, "auctionMsgConnectWallet"));
      return;
    }
    if (!minBidAmount) {
      setBidError(t(locale, "auctionMsgInvalidBid"));
      return;
    }
    if (bidCountdownSec > 0) {
      setBidError(
        `${t(locale, "auctionBidLockedMsg")} ${formatCountdownSec(bidCountdownSec)}`,
      );
      return;
    }

    try {
      setBid({ status: "loading", message: t(locale, "auctionMsgSubmitting") });

      if (!publicClient) {
        setBidError(t(locale, "profileMsgRpcUnavailableShort"));
        return;
      }

      // Refresh auction data before bidding to ensure we have the latest chain state
      const latestAuctionData = await apiGetAuctions({
        limit: 1,
        auctionId,
      });
      const latestAuction =
        latestAuctionData?.auction ??
        (Array.isArray(latestAuctionData?.auctions)
          ? latestAuctionData.auctions[0]
          : null);
      const latestStartSec = parseToSec(
        latestAuction?.start_time ?? 0,
      );
      const nowAtSubmit = Math.floor(Date.now() / 1000);
      if (latestStartSec > nowAtSubmit) {
        const remain = latestStartSec - nowAtSubmit;
        setBidError(
          `${t(locale, "auctionBidLockedMsg")} ${formatCountdownSec(remain)}`,
        );
        return;
      }

      let bidBase = resolveBidBaseWei(latestAuction);
      let bidStep = computeBidStepWei(bidBase);
      let bidValue = computeNextBidFromBaseWei(bidBase);
      const expectedQuoteAddress = normalizeLowerAddress(USDT_CONTRACT_ADDRESS);
      const dbQuoteAddress = normalizeLowerAddress(
        latestAuction?.quote_address ?? "",
      );
      if (dbQuoteAddress && dbQuoteAddress !== expectedQuoteAddress) {
        setBidError(
          t(
            locale,
            "auctionUsdtOnlyBidHint",
            "This auction is not quoted in USDT. Please create a USDT auction first.",
          ),
        );
        return;
      }
      try {
        const chainRecord = await publicClient.readContract({
          address: AUCTION_CONTRACT_ADDRESS,
          abi: auctionAbi,
          functionName: "auctionRecord",
          args: [BigInt(String(auctionId))],
        });
        const chainBidQuote = normalizeLowerAddress(
          chainRecord?.bidQuote ?? chainRecord?.[10] ?? "",
        );
        if (chainBidQuote && chainBidQuote !== expectedQuoteAddress) {
          setBid({
            status: "error",
            message: t(
              locale,
              "auctionUsdtOnlyBidHint",
              "This auction is not quoted in USDT. Please create a USDT auction first.",
            ),
          });
          return;
        }
        const chainStartPrice = BigInt(
          chainRecord?.startPrice ?? chainRecord?.[6] ?? 0,
        );
        const chainLastPrice = BigInt(
          chainRecord?.lastPrice ?? chainRecord?.[7] ?? 0,
        );
        const chainBase =
          chainLastPrice > 0n ? chainLastPrice : chainStartPrice;
        if (chainBase > 0n) {
          bidBase = chainBase;
          bidStep = computeBidStepWei(bidBase);
          bidValue = computeNextBidFromBaseWei(bidBase);
        }
      } catch (chainReadError) {
        console.warn(
          "[placeBid] read auctionRecord failed, fallback to DB pricing",
          chainReadError,
        );
      }
      if (bidValue <= 0n) {
        setBidError(t(locale, "auctionMsgInvalidBid"));
        return;
      }
      let bidAmount = formatEther(bidValue);

      const allowanceTarget = bidValue + bidStep * 20n;
      const ensureErc20Allowance = async (target) => {
        const allowance = await publicClient.readContract({
          address: USDT_CONTRACT_ADDRESS,
          abi: ERC20_ABI,
          functionName: "allowance",
          args: [walletAddress, AUCTION_CONTRACT_ADDRESS],
        });
        if (allowance < target) {
          const waitTxSuccess = async (hash) => {
            const receipt = await publicClient.waitForTransactionReceipt({
              hash,
              timeout: 120_000,
            });
            if (receipt?.status !== "success") {
              throw new Error("approve transaction reverted");
            }
          };

          const approveSpender = async (amount) => {
            const approveHash = await writeContractAsync({
              address: USDT_CONTRACT_ADDRESS,
              abi: ERC20_ABI,
              functionName: "approve",
              args: [AUCTION_CONTRACT_ADDRESS, amount],
            });
            await waitTxSuccess(approveHash);
          };

          setBid({
            status: "loading",
            message: t(
              locale,
              "marketMsgApprovingToken",
              "Approving USDT spend...",
            ),
          });
          const approveAmount =
            target > USDT_BID_APPROVAL_WEI ? target : USDT_BID_APPROVAL_WEI;

          try {
            await approveSpender(approveAmount);
          } catch (approveError) {
            // Some ERC20s require setting allowance to 0 before changing it.
            if (allowance > 0n) {
              await approveSpender(0n);
              await approveSpender(approveAmount);
            } else {
              throw approveError;
            }
          }

          const refreshedAllowance = await publicClient.readContract({
            address: USDT_CONTRACT_ADDRESS,
            abi: ERC20_ABI,
            functionName: "allowance",
            args: [walletAddress, AUCTION_CONTRACT_ADDRESS],
          });
          if (refreshedAllowance < target) {
            throw new Error(
              `USDT allowance insufficient after approve: ${refreshedAllowance.toString()} < ${target.toString()}`,
            );
          }
        }
      };
      await ensureErc20Allowance(allowanceTarget);

      const auctionIdBig = BigInt(String(auctionId));
      let bidRequest = null;
      const maxSimulateAttempts = 10;
      for (let attempt = 0; attempt < maxSimulateAttempts; attempt += 1) {
        try {
          const simulated = await publicClient.simulateContract({
            account: walletAddress,
            address: AUCTION_CONTRACT_ADDRESS,
            abi: auctionAbi,
            functionName: "placeBidErc20",
            args: [auctionIdBig, bidValue],
          });
          bidRequest = simulated.request;
          break;
        } catch (simulateError) {
          if (
            !isNotEnoughPaymentError(simulateError) ||
            attempt === maxSimulateAttempts - 1
          ) {
            throw simulateError;
          }
          let chainCandidate = 0n;
          try {
            const latestRecord = await publicClient.readContract({
              address: AUCTION_CONTRACT_ADDRESS,
              abi: auctionAbi,
              functionName: "auctionRecord",
              args: [auctionIdBig],
            });
            const latestStartPrice = BigInt(
              latestRecord?.startPrice ?? latestRecord?.[6] ?? 0,
            );
            const latestLastPrice = BigInt(
              latestRecord?.lastPrice ?? latestRecord?.[7] ?? 0,
            );
            const latestBase =
              latestLastPrice > 0n ? latestLastPrice : latestStartPrice;
            if (latestBase > 0n) {
              bidStep = computeBidStepWei(latestBase);
              chainCandidate = computeNextBidFromBaseWei(latestBase);
            }
          } catch (refreshError) {
            console.warn(
              "[placeBid] refresh auctionRecord failed on retry",
              refreshError,
            );
          }
          const stepped = bidValue + (bidStep > 0n ? bidStep : 1n);
          bidValue = chainCandidate > stepped ? chainCandidate : stepped;
          bidAmount = formatEther(bidValue);
          await ensureErc20Allowance(bidValue + bidStep * 20n);
          console.warn("[placeBid] bump bid after not_enough_payment", {
            attempt: attempt + 1,
            nextBidWei: bidValue.toString(),
            bidStepWei: bidStep.toString(),
          });
        }
      }
      if (!bidRequest) {
        throw new Error("Failed to build bid request");
      }

      const txHash = await writeContractAsync({
        ...bidRequest,
      });

      await publicClient.waitForTransactionReceipt({ hash: txHash });

      try {
        await apiNotifyAuctionTx({
          txHash,
          auctionId,
          action: "bid",
          amount: bidAmount,
          source: "auction-detail",
        });
      } catch (notifyError) {
        console.warn("[auction/notify] bid enqueue failed", notifyError);
      }

      setBid({ status: "success", message: t(locale, "auctionMsgBidSuccess") });
      auctionsQuery.refetch();
      bidsQuery.refetch();
      eventsQuery.refetch();
    } catch (error) {
      const rawErrorText = String(
        error?.shortMessage || error?.message || error || "",
      ).toLowerCase();
      if (rawErrorText.includes("noerc20_bid")) {
        setBidError(
          t(
            locale,
            "auctionUsdtOnlyBidHint",
            "This auction is not quoted in USDT. Please create a USDT auction first.",
          ),
        );
        return;
      }
      setBidError(
        `${t(locale, "auctionMsgBidFailed")}${String(error?.message || error)}`,
      );
    }
  };

  const runRefreshLatestBid = async () => {
    if (refreshingBid || !auctionId) return;
    setRefreshingBid(true);
    setRefreshTip("");
    try {
      await Promise.all([
        auctionsQuery.refetch(),
        bidsQuery.refetch(),
        eventsQuery.refetch(),
        rebatesQuery.refetch(),
      ]);
      if (publicClient && auctionId) {
        const chainRecord = await publicClient.readContract({
          address: AUCTION_CONTRACT_ADDRESS,
          abi: auctionAbi,
          functionName: "auctionRecord",
          args: [BigInt(String(auctionId))],
        });
        const chainStartPrice = BigInt(
          chainRecord?.startPrice ?? chainRecord?.[6] ?? 0,
        );
        const chainLastPrice = BigInt(
          chainRecord?.lastPrice ?? chainRecord?.[7] ?? 0,
        );
        const chainBase =
          chainLastPrice > 0n ? chainLastPrice : chainStartPrice;
        setChainBidBaseWei(chainBase > 0n ? chainBase : null);
      }
      setRefreshTip(
        `${t(locale, "auctionRefreshUpdated", "Latest bid data refreshed")} (${new Date().toLocaleTimeString()})`,
      );
    } catch (error) {
      console.warn("[auction/detail] refresh latest bid failed", error);
      setRefreshTip(
        t(locale, "auctionRefreshFailed", "Refresh failed. Please try again."),
      );
    } finally {
      setRefreshingBid(false);
    }
  };

  return (
    <>
      <Seo
        locale={locale}
        title={`Chronos Arte Node | ${t(locale, "navAuction")}`}
        description={t(locale, "auctionDesc")}
        path={buildPath(locale, `auction/${auctionId || ""}`)}
      />

      <Panel
        className="auction-detail-main-panel"
        title={t(locale, "auctionDetailTitle")}
        description={t(locale, "auctionDetailDesc")}
      >
        <div className="auction-toolbar">
          <div className="auction-toolbar-actions">
            <button
              className="btn btn-ghost"
              onClick={() => navigate(buildPath(locale, "auction"))}
            >
              {t(locale, "auctionBackToList")}
            </button>
          </div>
        </div>

        {bid.status !== "idle" && bid.message && (
          <div className={`status-banner ${bid.status}`}>
            {bid.status === "loading" && (
              <strong>{t(locale, "auctionStatusProcessing")}</strong>
            )}
            {bid.status === "success" && (
              <strong>{t(locale, "auctionStatusSuccess")}</strong>
            )}
            {bid.status === "error" && (
              <strong>{t(locale, "auctionStatusError")}</strong>
            )}
            <p>{bid.message}</p>
          </div>
        )}

        <div className="card-grid two-cols">
          <NftCard
            locale={locale}
            className="auction-detail-asset-card"
            image={auctionImage}
            galleryImages={auctionGalleryImages}
            title={artworkName}
            tokenId={auctionTokenId}
            nftAddress={resolveNftAddress(auction)}
            resolveTokenName
            price={formatAmount(auction?.highest_bid, quoteSymbol)}
            priceLabel={t(locale, "auctionColHighest")}
            badge={`${auctionTypeLabel} #${auctionId || "-"}`}
            badgeTone="success"
          />
          <article className="card-link" style={{ pointerEvents: "none" }}>
            <h3>{t(locale, "auctionDetailMeta")}</h3>
            <p>
              {t(locale, "auctionColHighest")}:{" "}
              {formatAmount(auction?.highest_bid, quoteSymbol)}
            </p>
            <p>
              {t(locale, "auctionColEndTime")}:{" "}
              {formatDateTime(auction?.end_time)}
            </p>
            <p>
              {t(locale, "auctionCountdownTitle")}:{" "}
              <strong>
                <AuctionCountdown
                  endTime={auction?.end_time}
                  nowMs={nowSec * 1000}
                  showLabel={false}
                />
              </strong>
            </p>
            <p>
              {t(locale, "auctionColHighestBidder")}:{" "}
              {auction?.highest_bidder ?? "-"}
            </p>
            <p>
              {t(locale, "auctionColMyBid")}: {formatAmount(myBid, quoteSymbol)}
            </p>
            {artworkDescription ? (
              <p className="auction-detail-description">{artworkDescription}</p>
            ) : null}
          </article>
        </div>

        <div
          className="auction-bid-box"
          style={{ maxWidth: 320, marginTop: 16 }}
        >
          {minBidAmount && (
            <p style={{ marginBottom: 8, color: "var(--muted)" }}>
              {t(locale, "auctionMinBidLabel")}:{" "}
              <strong>
                {minBidAmount} {quoteSymbol}
              </strong>
            </p>
          )}
          {bidCountdownSec > 0 && (
            <p style={{ marginBottom: 8, color: "var(--muted)" }}>
              {t(locale, "auctionBidUnlockCountdown")}:{" "}
              <strong>
                <AuctionCountdown
                  endTime={bidUnlockAtSec * 1000}
                  nowMs={nowSec * 1000}
                  showLabel={false}
                />
              </strong>
            </p>
          )}
          <div className="auction-bid-actions">
            <button
              className="btn btn-primary"
              onClick={runPlaceBid}
              disabled={bid.status === "loading" || !minBidAmount}
            >
              {bid.status === "loading"
                ? t(locale, "auctionBidSubmitting")
                : bidCountdownSec > 0
                  ? t(locale, "auctionBidCooling")
                  : t(locale, "auctionBidBtn")}
            </button>
            <button
              className="btn btn-ghost"
              onClick={runRefreshLatestBid}
              disabled={refreshingBid || bid.status === "loading"}
            >
              {refreshingBid
                ? t(locale, "auctionRefreshBtnLoading", "Refreshing...")
                : t(locale, "auctionRefreshBtn", "Refresh Latest Bid")}
            </button>
          </div>
          {refreshTip ? (
            <p className="auction-refresh-tip">{refreshTip}</p>
          ) : null}
        </div>
      </Panel>

      <Panel
        className="auction-detail-bids-panel"
        title={t(locale, "auctionBidRecordsTitle", "Bid Records")}
        description={t(
          locale,
          "auctionBidRecordsDesc",
          "All bid records for this auction. Repeated bids from the same wallet are listed separately.",
        )}
      >
        {eventsQuery.isPending && !mergedBidEvents.length ? (
          <div className="status-banner loading">
            <strong>{t(locale, "auctionStatusProcessing")}</strong>
            <p>{t(locale, "auctionHistoryLoading")}</p>
          </div>
        ) : mergedBidEvents.length ? (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t(locale, "auctionHistoryColTime", "Time")}</th>
                    <th>{t(locale, "auctionColHighestBidder", "Bidder")}</th>
                    <th>{t(locale, "auctionColBid", "Bid")}</th>
                    <th>{t(locale, "auctionRebateTx", "Tx Hash")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedBidEvents.map((row) => {
                    const bidder = resolveBidder(row) || "-";
                    const amount = Number(resolveAmountValue(row));
                    const txHash = resolveTxHash(row);
                    const isMe = myAddr && bidder.toLowerCase() === myAddr;
                    const createdAt = formatDateTime(
                      resolveCreatedAtValue(row),
                    );
                    return (
                      <tr
                        key={buildBidEventKey(row)}
                        className={isMe ? "auction-bid-row-me" : ""}
                      >
                        <td>{createdAt || "-"}</td>
                        <td className="mono" title={bidder}>
                          {bidder}
                        </td>
                        <td>
                          {amount > 0
                            ? `${formatTinyAmount(amount)} ${quoteSymbol}`
                            : "-"}
                        </td>
                        <td className="mono" title={txHash || "-"}>
                          {txHash ? (
                            <a
                              href={buildBscTxUrl(txHash)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {shortHash(txHash)}
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="auction-bid-pagination">
              <button
                className="btn btn-ghost"
                onClick={() => setBidPage((p) => Math.max(1, p - 1))}
                disabled={activeBidPage <= 1}
              >
                {t(locale, "auctionBidPrevPage", "Prev")}
              </button>
              <span className="auction-bid-page-info">
                {t(locale, "auctionBidPageInfo", "Page")} {activeBidPage} /{" "}
                {bidPageCount}
              </span>
              <button
                className="btn btn-ghost"
                onClick={() => setBidPage((p) => Math.min(bidPageCount, p + 1))}
                disabled={activeBidPage >= bidPageCount}
              >
                {t(locale, "auctionBidNextPage", "Next")}
              </button>
            </div>
          </>
        ) : (
          <div className="empty-state">
            {t(locale, "auctionBidRecordsEmpty", "No bid records yet")}
          </div>
        )}
      </Panel>

      <Panel
        className="auction-detail-rebate-panel"
        title={t(locale, "auctionRebateTitle")}
        description={t(locale, "auctionRebateDesc")}
      >
        {myRebateTotal > 0 && (
          <div className="status-banner success" style={{ marginBottom: 12 }}>
            <strong>
              {t(locale, "auctionRebateTotalLabel")}:{" "}
              {formatTinyAmount(myRebateTotal)} {quoteSymbol}
            </strong>
          </div>
        )}

        {rebatesQuery.isPending && !mergedRebates.length ? (
          <div className="status-banner loading">
            <strong>{t(locale, "auctionStatusProcessing")}</strong>
            <p>{t(locale, "auctionRebateLoading")}</p>
          </div>
        ) : mergedRebates.length ? (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t(locale, "auctionHistoryColTime", "Time")}</th>
                    <th>{t(locale, "auctionRebateRecipient")}</th>
                    <th>{t(locale, "auctionRebateAmount")}</th>
                    <th>{t(locale, "auctionRebateTx")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRebates.map((row) => {
                    const recipient = resolveRecipient(row) || "-";
                    const amount = Number(resolveAmountValue(row));
                    const txHash = resolveTxHash(row);
                    const isMe = myAddr && recipient.toLowerCase() === myAddr;
                    const createdAt = formatDateTime(
                      resolveCreatedAtValue(row),
                    );
                    return (
                      <tr
                        key={buildRebateDisplayKey(row)}
                        className={isMe ? "auction-rebate-row-me" : ""}
                      >
                        <td>{createdAt || "-"}</td>
                        <td className="mono" title={recipient}>
                          {recipient}
                        </td>
                        <td>
                          {amount > 0
                            ? `${formatTinyAmount(amount)} ${quoteSymbol}`
                            : "-"}
                        </td>
                        <td className="mono" title={txHash || "-"}>
                          {txHash ? (
                            <a
                              href={buildBscTxUrl(txHash)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {shortHash(txHash)}
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="auction-rebate-pagination">
              <button
                className="btn btn-ghost"
                onClick={() => setRebatePage((p) => Math.max(1, p - 1))}
                disabled={activeRebatePage <= 1}
              >
                {t(locale, "auctionRebatePrevPage", "Prev")}
              </button>
              <span className="auction-rebate-page-info">
                {t(locale, "auctionRebatePageInfo", "Page")} {activeRebatePage}{" "}
                / {rebatePageCount} {t(locale, "auctionRebatePageInfoTail", "")}
              </span>
              <button
                className="btn btn-ghost"
                onClick={() =>
                  setRebatePage((p) => Math.min(rebatePageCount, p + 1))
                }
                disabled={activeRebatePage >= rebatePageCount}
              >
                {t(locale, "auctionRebateNextPage", "Next")}
              </button>
            </div>
          </>
        ) : (
          <div className="empty-state">{t(locale, "auctionRebateEmpty")}</div>
        )}
      </Panel>
    </>
  );
}
